/**
 * The colour a collaborator is drawn in, everywhere they are drawn.
 *
 * A user's colour has to be the same on their node outline, on their choice
 * label, on the group and key rows, and on their presence chip in the TopBar —
 * that identity is the entire point of the colour. It was previously four
 * separate copies of this function (Canvas.js, SituationNode.js, CreateKey.js,
 * SituationsAndGroups.js) which had already drifted: only one of them memoised,
 * and only one of them handled an empty id, so an anonymous user was `'#NaNNaN'`
 * in three places out of four.
 *
 * The hash itself is deliberately unchanged from those copies. Authors have
 * learned "Sam is the green one"; a nicer palette would silently reassign every
 * colour in the product.
 */

const colorCache = new Map();

/**
 * A stable colour for an arbitrary string (in practice a user id).
 * @param {string} str
 * @returns {string} a `#rrggbb` colour
 */
export function hashStringToColor(str) {
    if (!str) return '#000000';

    const cached = colorCache.get(str);
    if (cached) return cached;

    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
        hash = hash & hash;
    }
    const color = `#${((hash >>> 0) % 0xffffff).toString(16).padStart(6, '0')}`;

    colorCache.set(str, color);
    return color;
}

/**
 * Whether white or black text is legible on a background colour.
 *
 * Presence chips are filled with a hashed colour that can land anywhere in the
 * space, so the initials on top cannot use a fixed foreground. Rec. 601 luma,
 * which is the usual cheap approximation and more than enough for two-letter
 * labels.
 *
 * @param {string} hexColor - `#rrggbb`
 * @returns {string} `'#ffffff'` or `'#000000'`
 */
export function readableTextColor(hexColor) {
    const hex = typeof hexColor === 'string' ? hexColor.replace('#', '') : '';
    if (hex.length !== 6) return '#ffffff';

    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return '#ffffff';

    return (r * 299 + g * 587 + b * 114) / 1000 > 140 ? '#000000' : '#ffffff';
}

/**
 * Up to two initials for a collaborator chip.
 *
 * Falls back through name → email → a bare dot, because presence records are
 * built from whatever the joining client knew about itself and a name is not
 * guaranteed to be one of those things.
 *
 * @param {{userName?: string, userEmail?: string}} [collaborator]
 * @returns {string}
 */
export function initialsFor(collaborator = {}) {
    const source = collaborator.userName?.trim() || collaborator.userEmail?.trim() || '';
    if (!source) return '·';

    const words = source.split(/[\s._-]+/).filter(Boolean);
    if (words.length === 0) return '·';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

/** The name to show for a collaborator, with the same fallbacks as the initials. */
export function displayNameFor(collaborator = {}) {
    return collaborator.userName?.trim() || collaborator.userEmail?.trim() || 'Someone';
}
